/**
 * Platform-agnostic theme tokens — the source of truth for the theme
 * names and the single brand accent each theme contributes.
 *
 * The web apps consume the full token set as CSS (`theme.css`, imported
 * via `@myosh/design-system/styles`); this module carries the slice that
 * non-CSS consumers (React Native) need: the accent (`primary-2`, the
 * most-used ramp step) per theme and colour scheme. The `oklch` strings
 * are byte-identical to `theme.css` and pinned to it by test; the `hex`
 * values are their sRGB equivalents for renderers without `oklch()`
 * support. No DOM access — safe to import anywhere.
 */

export const THEME_NAMES = ['blue', 'brown', 'green', 'grey', 'orange', 'purple', 'red', 'yellow'] as const;

export type ThemeName = (typeof THEME_NAMES)[number];

export const DEFAULT_THEME: ThemeName = 'blue';

/** Narrow an arbitrary string (e.g. the backend `company-theme`) to a known theme. */
export const toThemeName = (value: string | undefined | null): ThemeName => {
  return THEME_NAMES.includes(value as ThemeName) ? (value as ThemeName) : DEFAULT_THEME;
};

export interface ThemeAccent {
  /** `primary-2` exactly as written in theme.css, per colour scheme. */
  oklch: { light: string; dark: string };
  /** sRGB hex equivalents of `oklch` (verified by test). */
  hex: { light: string; dark: string };
  /**
   * Ink for text/icons rendered on the accent, chosen for contrast
   * against `hex`. Not part of the web CSS (web composes its own
   * foregrounds); defined here so non-CSS consumers agree on it.
   */
  foregroundHex: { light: string; dark: string };
}

export const THEME_ACCENTS: Record<ThemeName, ThemeAccent> = {
  blue: {
    oklch: { light: 'oklch(0.48 0.201 260.48)', dark: 'oklch(0.66 0.201 260.48)' },
    hex: { light: '#0052cc', dark: '#428cff' },
    foregroundHex: { light: '#ffffff', dark: '#020c22' },
  },
  brown: {
    oklch: { light: 'oklch(0.427 0.034 61.52)', dark: 'oklch(0.66 0.034 61.52)' },
    hex: { light: '#5d4b3c', dark: '#a28e7d' },
    foregroundHex: { light: '#ffffff', dark: '#140b05' },
  },
  green: {
    oklch: { light: 'oklch(0.636 0.2 145.01)', dark: 'oklch(0.66 0.2 145.01)' },
    hex: { light: '#00a82d', dark: '#19b036' },
    foregroundHex: { light: '#000b01', dark: '#000b01' },
  },
  grey: {
    oklch: { light: 'oklch(0.59 0 0)', dark: 'oklch(0.66 0 0)' },
    hex: { light: '#7d7d7d', dark: '#929292' },
    foregroundHex: { light: '#0a0a0a', dark: '#0a0a0a' },
  },
  orange: {
    oklch: { light: 'oklch(0.751 0.177 57.28)', dark: 'oklch(0.66 0.177 57.28)' },
    hex: { light: '#ff8c14', dark: '#df6f00' },
    foregroundHex: { light: '#1e0c01', dark: '#1e0c01' },
  },
  purple: {
    oklch: { light: 'oklch(0.35 0.148 297.52)', dark: 'oklch(0.66 0.148 297.52)' },
    hex: { light: '#461e7c', dark: '#9d7ce0' },
    foregroundHex: { light: '#ffffff', dark: '#0f091c' },
  },
  red: {
    oklch: { light: 'oklch(0.525 0.215 29.23)', dark: 'oklch(0.66 0.215 29.23)' },
    hex: { light: '#c90101', dark: '#fa4a3a' },
    foregroundHex: { light: '#ffffff', dark: '#190403' },
  },
  yellow: {
    oklch: { light: 'oklch(0.865 0.177 90.38)', dark: 'oklch(0.66 0.177 90.38)' },
    hex: { light: '#ffcc00', dark: '#bb8a00' },
    foregroundHex: { light: '#1f1400', dark: '#1f1400' },
  },
};
